import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { Flex } from "@redotech/redo-web/flex";
import { TabsPortal } from "@redotech/redo-web/tabs-portal";
import { memo, useState } from "react";

import { MarketingFlagKey } from "@redotech/feature-flags-keys-marketing/keys";
import InfoIcon from "@redotech/redo-icons/info-circle.svg";
import {
  analyticsFiltersFromSettings,
  attributionEventsApply,
  DEFAULT_ATTRIBUTION_DAYS,
  type AnalyticsAttributionFilters,
} from "@redotech/redo-marketing-common/analytics-attribution-filters";
import {
  marketingAttributionEvent,
  type MarketingAttributionEvent,
} from "@redotech/redo-marketing-templates-common/marketing";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { useTeamFeatureFlag } from "@redotech/redo-merchant-app-common/use-team-feature-flag";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { createTabConfig, useTabs } from "@redotech/redo-web/utils/use-tabs";
import { defaultAnalyticsDateFilter } from "./analytics-date-range";
import { MarketingAttributionEventsFilter } from "./filters/marketing-attribution-events-filter";
import { MarketingAttributionFilter } from "./filters/marketing-attribution-filter";
import { BotFilter, MarketingBotFilter } from "./filters/marketing-bot-filter";
import {
  ChannelFilter,
  MarketingChannelFilter,
} from "./filters/marketing-channel-filter";
import { MarketingDateRangeFilter } from "./filters/marketing-date-range-filter";
import { MarketingAnalyticsAudience } from "./marketing-analytics-sections/marketing-analytics-audience";
import { MarketingAnalyticsBackInStock } from "./marketing-analytics-sections/marketing-analytics-back-in-stock";
import { MarketingAnalyticsOverview } from "./marketing-analytics-sections/marketing-analytics-overview";

export enum MarketingAnalyticsPage {
  OVERVIEW = "overview",
  AUDIENCE = "audience",
  BACK_IN_STOCK = "back-in-stock",
}

export const MARKETING_ANALYTICS_TABS_URL_PARAM = "tab";

const tabConfig = createTabConfig(MarketingAnalyticsPage, {
  [MarketingAnalyticsPage.OVERVIEW]: { component: MarketingAnalyticsOverview },
  [MarketingAnalyticsPage.AUDIENCE]: { component: MarketingAnalyticsAudience },
  [MarketingAnalyticsPage.BACK_IN_STOCK]: {
    component: MarketingAnalyticsBackInStock,
  },
});

/** The mart splits clicks by bot verdict and nothing else, so the filter must
 *  say what it did not touch. */
const BOT_FILTER_SCOPE_NOTE =
  "Opens, sends, orders, and revenue on this page still count all activity.";

const DEFAULT_ATTRIBUTION_EVENTS: MarketingAttributionEvent[] = [
  marketingAttributionEvent.CLICK,
  marketingAttributionEvent.OPEN,
  marketingAttributionEvent.DELIVERY,
];

/** Saved settings only affect ClickHouse, so teams still on Snowflake get
 *  these instead. */
const DEFAULT_FILTERS: AnalyticsAttributionFilters = {
  attributionDays: DEFAULT_ATTRIBUTION_DAYS,
  attributionEvents: DEFAULT_ATTRIBUTION_EVENTS,
};

/** SMS opens aren't tracked, so that channel hides the Opens checkbox. */
const EVENTS_BY_CHANNEL: Record<ChannelFilter, MarketingAttributionEvent[]> = {
  all: DEFAULT_ATTRIBUTION_EVENTS,
  [MarketingChannel.EMAIL]: DEFAULT_ATTRIBUTION_EVENTS,
  [MarketingChannel.SMS]: [
    marketingAttributionEvent.CLICK,
    marketingAttributionEvent.DELIVERY,
  ],
};

export const MarketingAnalytics = memo(function MarketingAnalytics() {
  const { enabled: attributionEnabled, pending: flagPending } =
    useTeamFeatureFlag(
      MarketingFlagKey.MARKETING_ATTRIBUTION_METRICS_CLICKHOUSE,
      { forceEnabledForRedoUsers: false },
    );
  const { data: attributionSettings, isPending: settingsPending } = useRpcQuery(
    RedoMarketingRpcClientContext,
    "getAttributionSettings",
    {},
    { enabled: attributionEnabled },
  );
  // Settings supply the starting values only; an edit below takes over. Teams
  // on Snowflake, and a settings request that failed, get the defaults.
  const startingFilters =
    flagPending || (attributionEnabled && settingsPending)
      ? undefined
      : attributionSettings
        ? analyticsFiltersFromSettings(attributionSettings)
        : DEFAULT_FILTERS;
  const [editedFilters, setEditedFilters] =
    useState<AnalyticsAttributionFilters>();
  const filters = editedFilters ?? startingFilters;

  // The direct model credits touchpoints, not cause events, so the events
  // filter would sit there doing nothing.
  const eventsApply = attributionSettings
    ? attributionEventsApply(attributionSettings)
    : true;
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>("all");
  const [botFilter, setBotFilter] = useState<BotFilter>("all");
  const selectedEvents = filters?.attributionEvents ?? [];
  // Hiding by channel is computed, not saved, so switching back to all
  // channels brings the hidden checkbox back.
  const visibleEvents = EVENTS_BY_CHANNEL[channelFilter];
  const attributionEvents = selectedEvents.filter((event) =>
    visibleEvents.includes(event),
  );
  // The filter only reports checkboxes this channel shows, so keep the hidden
  // ones instead of treating them as unchecked.
  const selectVisibleEvents = (events: MarketingAttributionEvent[]) =>
    filters &&
    setEditedFilters({
      ...filters,
      attributionEvents: [
        ...events,
        ...filters.attributionEvents.filter(
          (event) => !visibleEvents.includes(event),
        ),
      ],
    });
  const setAttributionDays = (attributionDays: number) =>
    filters && setEditedFilters({ ...filters, attributionDays });

  const [dateFilter, setDateFilter] = useState<
    [Temporal.PlainDate, Temporal.PlainDate] | undefined
  >(defaultAnalyticsDateFilter);

  const { selectedTab, setSelectedTab, tabs, SelectedTabComponent } = useTabs({
    tabConfig,
    urlParam: MARKETING_ANALYTICS_TABS_URL_PARAM,
  });

  return (
    <Flex
      dir="column"
      gap={
        selectedTab === MarketingAnalyticsPage.BACK_IN_STOCK ? "none" : "4xl"
      }
      w="full"
    >
      <TabsPortal>
        <RedoHorizontalTabs<MarketingAnalyticsPage>
          drawBottomLine={false}
          pl="none"
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          size="xs"
          tabs={tabs}
        />
      </TabsPortal>
      {!filters ? (
        <Flex justify="center">
          <RedoSpinner />
        </Flex>
      ) : (
        <>
          {selectedTab !== MarketingAnalyticsPage.BACK_IN_STOCK && (
            <Flex dir="column" gap="md">
              <Flex dir="row" gap="md">
                {selectedTab === MarketingAnalyticsPage.OVERVIEW && (
                  <>
                    <MarketingAttributionFilter
                      attributionFilter={filters.attributionDays}
                      setAttributionFilter={setAttributionDays}
                    />
                    <MarketingChannelFilter
                      channelFilter={channelFilter}
                      setChannelFilter={setChannelFilter}
                    />
                    <MarketingBotFilter
                      botFilter={botFilter}
                      setBotFilter={setBotFilter}
                    />
                    {eventsApply && (
                      <MarketingAttributionEventsFilter
                        channelFilter={channelFilter}
                        onChange={selectVisibleEvents}
                        selectedEvents={attributionEvents}
                      />
                    )}
                  </>
                )}
                {selectedTab === MarketingAnalyticsPage.AUDIENCE && (
                  <MarketingChannelFilter
                    channelFilter={channelFilter}
                    setChannelFilter={setChannelFilter}
                  />
                )}
                <MarketingDateRangeFilter
                  dateFilter={dateFilter}
                  setDateFilter={setDateFilter}
                />
              </Flex>
              {selectedTab === MarketingAnalyticsPage.OVERVIEW &&
                botFilter !== "all" && (
                  <RedoAlert
                    color="gray"
                    description={BOT_FILTER_SCOPE_NOTE}
                    Icon={InfoIcon}
                    size="sm"
                    title="Bot filter applies to click metrics only"
                  />
                )}
            </Flex>
          )}
          <SelectedTabComponent
            attributionDays={filters.attributionDays}
            attributionEvents={attributionEvents}
            botFilter={botFilter}
            channelFilter={channelFilter}
            dateFilter={dateFilter}
          />
        </>
      )}
    </Flex>
  );
});
